import type { MeResponse } from "@agw/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router";
import { canManagePeople, canSeeReports, canSubmit, isAdmin } from "../features/auth/useMe";
import { OfflineBanner } from "../features/offline/OfflineBanner";
import { useOutboxReplay } from "../features/offline/useOutbox";
import { api } from "../lib/endpoints";
import { ROLE_LABEL } from "../lib/labels";
import { Icon, type IconName } from "../ui/Icon";

interface NavItem { to: string; label: string; icon: IconName; end?: boolean }
interface NavGroup { title: string; items: NavItem[] }

/** The menu for this login. The API enforces the same rules; this only hides what they can't use. */
function navGroups(me: MeResponse): NavGroup[] {
  const work: NavItem[] = [
    ...(canSeeReports(me) ? [{ to: "/reports", label: "Reports", icon: "reports" as const }] : []),
    { to: "/jobs", label: "Claims", icon: "claims", end: true },
  ];
  const manage: NavItem[] = [
    ...(canManagePeople(me) ? [{ to: "/people", label: "Team", icon: "people" as const }] : []),
    ...(canManagePeople(me) ? [{ to: "/dealerships", label: "Dealerships", icon: "store" as const }] : []),
    ...(isAdmin(me) ? [{ to: "/ops", label: "Operations", icon: "ops" as const }] : []),
  ];
  return [
    { title: "Work", items: work },
    ...(manage.length ? [{ title: "Manage", items: manage }] : []),
    { title: "Account", items: [{ to: "/settings", label: "Settings", icon: "settings" }] },
  ];
}

const TITLES: [RegExp, string][] = [
  [/^\/reports/, "Reports"], [/^\/jobs\/new/, "New claim"], [/^\/jobs\/./, "Claim"], [/^\/jobs/, "Claims"],
  [/^\/people/, "Team"], [/^\/dealerships/, "Dealerships"], [/^\/ops/, "Operations"], [/^\/settings/, "Settings"],
  [/^\/help\/install/, "Add to home screen"], [/^\/qr/, "Service desk QR"],
];
/** Forms and single records read better in one column, even on a wide screen. */
const NARROW = /^\/(jobs\/.+|settings|help|qr)/;
const titleFor = (path: string) => TITLES.find(([re]) => re.test(path))?.[1] ?? "Dealer portal";
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");

/** Matches the sidebar breakpoint in app.css. */
const DESKTOP = "(min-width: 1024px)";
const COLLAPSE_KEY = "agw.nav.collapsed";
const readCollapsed = () => { try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; } };

export function Layout({ me }: { me: MeResponse }) {
  const qc = useQueryClient();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false); // phone drawer
  const [collapsed, setCollapsed] = useState(readCollapsed); // desktop rail
  useOutboxReplay(me.userId);

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open]);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      try { localStorage.setItem(COLLAPSE_KEY, c ? "0" : "1"); } catch { /* private mode */ }
      return !c;
    });
  };

  const logout = async () => {
    await api.logout().catch(() => undefined);
    qc.clear();
    window.location.assign("/login");
  };

  const roleLabel = me.roles.map((r) => ROLE_LABEL[r]).join(", ");
  const title = titleFor(pathname);

  return (
    <div className={`app${collapsed ? " app--collapsed" : ""}${open ? " app--open" : ""}`}>
      <a className="skip-link" href="#main">Skip to content</a>
      <aside id="app-nav" className="nav" aria-label="Main menu">
        <div className="nav__brand">
          <img src="/logo.png" alt="" width={32} height={27} className="nav__logo" />
          <div className="nav__brand-text">
            <div className="nav__name">Auto Glass World</div>
            <div className="nav__product">Dealer portal</div>
          </div>
          <button type="button" className="nav__close" onClick={() => setOpen(false)} aria-label="Close menu">
            <Icon name="close" />
          </button>
        </div>

        {canSubmit(me) && (
          <NavLink to="/jobs/new" className="nav__cta" title="New claim">
            <Icon name="plus" /><span className="nav__label">New claim</span>
          </NavLink>
        )}

        <nav className="nav__groups">
          {navGroups(me).map((g) => (
            <div key={g.title} className="nav__group">
              <div className="nav__group-title">{g.title}</div>
              {g.items.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.end} className="nav__item" title={it.label}>
                  <Icon name={it.icon} /><span className="nav__label">{it.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="nav__footer">
          <div className="nav__user" title={`${me.name}, ${roleLabel}`}>
            <span className="avatar" aria-hidden="true">{initials(me.name)}</span>
            <span className="nav__user-text">
              <span className="nav__user-name">{me.name}</span>
              <span className="nav__user-role">{roleLabel}</span>
            </span>
          </div>
          <button type="button" className="nav__item nav__signout" onClick={logout} title="Sign out">
            <Icon name="logout" /><span className="nav__label">Sign out</span>
          </button>
          <button type="button" className="nav__item nav__collapse" onClick={toggleCollapsed}
            aria-label={collapsed ? "Expand menu" : "Collapse menu"} title={collapsed ? "Expand menu" : "Collapse menu"}>
            <Icon name={collapsed ? "expand" : "collapse"} /><span className="nav__label">Collapse</span>
          </button>
        </div>
      </aside>

      <div className="scrim" onClick={() => setOpen(false)} aria-hidden="true" />

      <div className="main">
        <header className="appbar">
          {/* Phone: opens the drawer. Desktop: folds the sidebar to an icon rail and back. */}
          <button type="button" className="appbar__menu"
            onClick={() => (window.matchMedia(DESKTOP).matches ? toggleCollapsed() : setOpen(true))}
            aria-label="Menu" aria-expanded={open || !collapsed} aria-controls="app-nav">
            <Icon name="menu" size={22} />
          </button>
          <div className="appbar__title" aria-hidden="true">{title}</div>
          {/* Only on the claim screens: elsewhere the page's own action leads, and the menu has New claim. */}
          {canSubmit(me) && (pathname === "/jobs" || pathname.startsWith("/jobs/")) && pathname !== "/jobs/new" && (
            <NavLink to="/jobs/new" className="appbar__action"><Icon name="plus" size={18} /><span>New claim</span></NavLink>
          )}
        </header>
        <OfflineBanner ownerId={me.userId} />
        <main id="main" tabIndex={-1} className={`content${NARROW.test(pathname) ? " content--narrow" : ""}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
